import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CircularProgress, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";
import toast from "react-hot-toast";
import apiService from "services/api";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function BulkOrders() {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getBulkOrders();
      const result = response?.data;
      if (!result?.success) {
        throw new Error(result?.message || result?.error || "Failed to fetch bulk orders");
      }
      setOrders(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to fetch bulk orders"
      );
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const table = useMemo(() => {
    const columns = [
      { name: "member", align: "left" },
      { name: "thali", align: "left" },
      { name: "qty", align: "center" },
      { name: "orderDate", align: "left" },
      { name: "createdAt", align: "left" },
    ];

    const rows = orders.map((order) => ({
      member: (
        <SoftBox>
          <SoftTypography variant="button" fontWeight="medium">
            {order.memberId?.name || "—"}
          </SoftTypography>
          <SoftTypography variant="caption" color="secondary">
            {order.memberId?.phone ? `+91${order.memberId.phone}` : "—"}
          </SoftTypography>
        </SoftBox>
      ),
      thali: (
        <SoftTypography variant="caption" color="text">
          {order.thaliId?.name || "—"}
        </SoftTypography>
      ),
      qty: (
        <SoftTypography variant="button" fontWeight="medium">
          {order.qty || "—"}
        </SoftTypography>
      ),
      orderDate: (
        <SoftTypography variant="caption" color="text">
          {order.orderDate || "—"}
        </SoftTypography>
      ),
      createdAt: (
        <SoftTypography variant="caption" color="text">
          {formatDate(order.createdAt)}
        </SoftTypography>
      ),
    }));

    return { columns, rows };
  }, [orders]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <SoftBox>
            <SoftTypography variant="h5" fontWeight="bold">
              Bulk Orders
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Member bulk thali order requests
            </SoftTypography>
          </SoftBox>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchOrders} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </SoftBox>

        <Card>
          <SoftBox p={2}>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <SoftBox display="flex" justifyContent="center" py={6}>
                  <CircularProgress size={32} />
                </SoftBox>
              ) : table.rows.length === 0 ? (
                <SoftBox py={6} textAlign="center">
                  <SoftTypography variant="body2" color="text">
                    No bulk orders found.
                  </SoftTypography>
                </SoftBox>
              ) : (
                <Table columns={table.columns} rows={table.rows} />
              )}
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default BulkOrders;
